'use client';

import { useState, useRef } from 'react';
import Image from 'next/image';
import { cn } from '@/core/utils/cn';
import { ImagePlus, X } from 'lucide-react';
import { useTranslation } from '@/core/i18n';

interface ImageUploadProps {
  label?: string;
  maxImages?: number;
  className?: string;
}

export function ImageUpload({ label, maxImages = 6, className }: ImageUploadProps) {
  const [previews, setPreviews] = useState<string[]>([]);
  const inputRef = useRef<HTMLInputElement>(null);

  const handleFiles = (files: FileList | null) => {
    if (!files) return;
    const newPreviews = Array.from(files)
      .slice(0, maxImages - previews.length)
      .map((file) => URL.createObjectURL(file));
    setPreviews((prev) => [...prev, ...newPreviews]);
  };
  const { t } = useTranslation();

  const removeImage = (index: number) => {
    setPreviews((prev) => prev.filter((_, i) => i !== index));
  };

  return (
    <div className={cn('space-y-2', className)}>
      {label && <label className="block text-[12px] font-medium text-hms-textSub">{label}</label>}
      <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
        {previews.map((src, i) => (
          <div key={i} className="relative aspect-square rounded-lg overflow-hidden bg-hms-bg border border-hms-border group" style={{ position: 'relative' }}>
            <Image src={src} alt={`Upload ${i + 1}`} fill sizes="(max-width: 640px) 33vw, 25vw" className="object-cover" unoptimized />
            <button id="shared-image-upload-btn-1"
              onClick={() => removeImage(i)}
              className="absolute top-1 right-1 w-5 h-5 rounded-full bg-black/60 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
            >
              <X className="w-3 h-3 text-white" />
            </button>
          </div>
        ))}
        {previews.length < maxImages && (
          <button id="shared-image-upload-btn-2"
            onClick={() => inputRef.current?.click()}
            className="aspect-square rounded-lg border-2 border-dashed border-hms-border flex flex-col items-center justify-center gap-1 hover:border-hms-textDim transition-colors"
          >
            <ImagePlus className="w-5 h-5 text-hms-textDim" />
            <span className="text-[10px] text-hms-textDim">{t('shared.imageUpload.addPhoto')}</span>
          </button>
        )}
      </div>
      <input id="shared-image-upload-file-1"
        ref={inputRef}
        type="file"
        accept="image/*"
        multiple
        onChange={(e) => handleFiles(e.target.files)}
        className="hidden"
      />
    </div>
  );
}
